import React, { useContext, Fragment, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spin, notification } from 'antd';
import 'antd/dist/reset.css';
import store from 'store';

import { LayoutContext } from '../../contexts';
import { handleRequestError } from '../../utils/API';
import { baseRoutes, authRoutes } from '../../router';
import { URL_BASE_NAME } from '../../constants';
import AuthGuard from '../../components/AuthGuard/AuthGuard';

import '../../assets/scss/index.scss';
import MainLayout from '../../layout/MainLayout';

// Component to handle errors and navigation (must be inside Router)
function ErrorHandler() {
  const { error, successNotification } = useContext(LayoutContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!error) return;

    const errorMessage = handleRequestError(error);
    const isAuthError = errorMessage === 'authentication failed';
    const is401Error = error.response && error.response.status === 401;

    // Handle 401 errors or authentication failed messages
    if (is401Error || isAuthError) {
      // Clear authentication data
      store.set('authenticationToken', null);
      store.set('username', null);

      // Redirect to login
      navigate('/login', { replace: true });
      return;
    }

    // Handle other errors with notification
    notification.error({
      placement: 'bottomRight',
      message: 'An error occured!',
      duration: 3,
      description: errorMessage,
    });
  }, [error, navigate]);

  useEffect(() => {
    !!successNotification &&
      notification.success({
        placement: 'bottomRight',
        message: successNotification,
        duration: 3,
      });
  }, [successNotification]);

  return null; // This component doesn't render anything
}

function AppContainer({ history }) {
  const authToken = store.get('authenticationToken');
  const username = store.get('username');

  const { loading, setUsernameComponent } = useContext(LayoutContext);

  useEffect(() => {
    username && setUsernameComponent(username);

    return () => {
      setUsernameComponent(null);
    };
  }, []);

  return (
    <Fragment>
      <Spin spinning={loading}>
        <Router basename={URL_BASE_NAME}>
          <ErrorHandler />
          <Routes>
            {baseRoutes.map(route => (
              <Route key={route.path} path={route.path} element={<route.component />} />
            ))}
            <Route path="/*" element={
              <AuthGuard>
                <MainLayout />
              </AuthGuard>
            } />
          </Routes>
        </Router>
      </Spin>
    </Fragment>
  );
}

export default AppContainer;
